import { DownOutlined, FileDoneOutlined, InfoCircleOutlined } from "@ant-design/icons";
import {
  Breadcrumb,
  Button,
  Divider,
  Dropdown,
  Empty,
  Layout,
  Result,
  Skeleton,
  Space,
  Table,
  notification,
  DatePicker,
  theme,
  Form
} from "antd";
import dayjs from "dayjs";
import {useEffect, useState} from "react";
import { Link, useNavigate } from "react-router-dom";
import { useIsMobileHook } from "../../../../utils/WindowHooks";
import {formatDateTime, formatPrice} from "../../../../utils/format";
import "./DepositListing.scss";
import AccountsSearchSelect from "../../../common/AccountsSearchSelect/AccountsSearchSelect";
import {apiDepositList, apiDepositsDownload} from "../../../../api/deposits";

const { RangePicker } = DatePicker;
const { Content } = Layout;

const DepositListing = () => {
  const [dataSales, setDataSale] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const isMobile = useIsMobileHook(1600);
  const [filteredData, setFilteredData] = useState(dataSales?.results);
  const [pagination, setPagination] = useState({
    current: 1,
    total: 0,
  });
  const navigate = useNavigate();
  const [activeError, setActiveError] = useState(false);
  const [errorStatus, setErrorStatus] = useState("");
  const [dateRange, setDateRange] = useState([
        dayjs().subtract(7, 'day'),
        dayjs().add(1, 'day')
      ]);
    const [sellerFilter, setSellerFilter] = useState();
  const [downloadLoading, setDownloadLoading] = useState(false);

  const handleRowClick = (record) => {
    const id = record.id;
    const newExpandedRowKeys = [...expandedRowKeys];
    const index = newExpandedRowKeys.indexOf(id);
    if (index > -1) {
      newExpandedRowKeys.splice(index, 1);
    } else {
      newExpandedRowKeys.push(id);
    }
    setExpandedRowKeys(newExpandedRowKeys);
  };

  const fetchData = async (params) => {
    setIsFetching(true);
    try {
      const filteredParams = Object.fromEntries(
        Object.entries(params).filter(([_, value]) => value !== undefined && value !== null && value !== '')
      );
      const json = await apiDepositList(filteredParams);
      setDataSale(json);
      setFilteredData(json.results);
      setPagination((prevPagination) => ({
        ...prevPagination,
        total: json.count,
        current: params.page,
      }));
    } catch (error) {
      setActiveError(true);
      setErrorStatus(error.status);
      notification.error({
        message: "Erro",
        description: "Ocorreu um erro ao buscar os recebiveis.",
        placement: "topRight",
      });
    } finally {
      setIsFetching(false);
    }
  };

  const onChangePage = (pagination) => {
    fetchData({
      page: pagination.current,
      account: sellerFilter ? sellerFilter.id : undefined,
      created_at_gte: dateRange[0]?.format("YYYY-MM-DD") || undefined,
      created_at_lte: dateRange[1]?.format("YYYY-MM-DD") || undefined,
    });
  };

  useEffect(() => {
    setPagination({
      current: 1,
      total: 0,
    })
    fetchData({
      account: sellerFilter ? sellerFilter.id : undefined,
      created_at_gte: dateRange[0]?.format("YYYY-MM-DD") || undefined,
      created_at_lte: dateRange[1]?.format("YYYY-MM-DD") || undefined,
    });
  }, [sellerFilter, dateRange]);


  const items = [
    {
      label: 'CSV',
      key: 'csv',
      icon: <FileDoneOutlined />,
    },
    {
      label: 'EXCEL',
      key: 'excel',
      icon: <FileDoneOutlined />,
    },
  ];

  const handleDownload = async (format) => {
    const filters = {
      account: sellerFilter ? sellerFilter.id : undefined,
      created_at_gte: dateRange[0]?.format("YYYY-MM-DD") || undefined,
      created_at_lte: dateRange[1]?.format("YYYY-MM-DD") || undefined,
    };

    const filteredParams = Object.fromEntries(
      Object.entries(filters).filter(([_, value]) => value !== undefined)
    );

    try {
      setDownloadLoading(true);
      const blob = await apiDepositsDownload(format, filteredParams);
      const date = new Date().toISOString().slice(0, 10);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Depositos-${date}.${format === 'csv' ? 'csv' : 'xlsx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Failed to download file:', error);
      notification.error({
        message: "Erro",
        description: "Ocorreu um erro ao gerar o relatório.",
        placement: "topRight",
      });
    } finally {
      setDownloadLoading(false);
    }
  };

  const reportMenuProps = {
    items,
    onClick: (e) => {
      handleDownload(e.key);
    },
  };

  const statusMap = {
    confirmed: <span className="badge badge-success">Confirmada</span>,
    succeeded: <span className="badge badge-success">Sucesso</span>,
    created: <span className="badge yellow">Criada</span>,
    pending: <span className="badge yellow">Pendente</span>,
    canceled: <span className="badge badge-error">Cancelada</span>,
    failed: <span className="badge badge-error">Falhou</span>,
  };

  const renderStatus = (status, obj) => {
    if (status === "succeeded" && obj.confirmed) {
      status = "confirmed"
    }
    return statusMap[status] || <span className="badge black">Indefinido</span>;
  };

  const columns = [
    {
      title: "Data/Hora",
      dataIndex: "created_at",
      key: "created_at",
      onCell: () => ({
        style: { cursor: "pointer" },
      }),
      render: (confirmed) => (
        <p
          style={{ textTransform: "capitalize" }}
          dangerouslySetInnerHTML={{ __html: formatDateTime(confirmed) }}
        />
      ),
    },
    {
      title: "Identificador",
      dataIndex: "id",
      key: "id",
      onCell: () => ({
        style: { cursor: "pointer" },
      }),
      responsive: ["sm"],
    },
    {
      title: "Destino",
      dataIndex: ["account", "display_name"],
      key: "account__display_name",
      responsive: ["xxl"],
    },
    {
      title: "Descrição",
      dataIndex: "description",
      key: "description",
      responsive: ["xxl"],
    },
    {
      title: "Valor",
      dataIndex: "amount",
      key: "amount",
      render: (confirmed) => (
        <p
          style={{ textTransform: "capitalize" }}
          dangerouslySetInnerHTML={{ __html: formatPrice(confirmed) }}
        />
      ),
    },
    {
      title: "Cód. Aut",
      dataIndex: "transfer_number",
      key: "transfer_number",
      responsive: ["sm"],
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: renderStatus,
      responsive: ["sm"],
    },
    {
      title: "",
      dataIndex: "",
      render: (confirme) => (
        <Link to={`/deposito/${confirme.id}`}>
          <InfoCircleOutlined style={{ cursor: "pointer", height: "100%" }} />
        </Link>
      ),
      key: "",
      responsive: ["lg"],
    },
  ];

  const expandedRowRender = (record) => (
    <div className="expandid">
      <span>
       Data/Hora:{" "}
        <p
          dangerouslySetInnerHTML={{ __html: formatDateTime(record.created_at) }}
        />
      </span>
      <span>
        Identificador:{" "}
        <p>{record.id}</p>
      </span>
      <span>
        Destino:{" "}
        <p>{record.account ? record.account.display_name : ""}</p>
      </span>
      <span>
        Descrição:{" "}
        <p>{record.description}</p>
      </span>
      <span>
        Cód. Aut:{" "}
        <p>{record.transfer_number}</p>
      </span>
      <span>
        Valor:{" "}
        <p dangerouslySetInnerHTML={{ __html: formatPrice(record.amount) }} />
      </span>
      <span>
        Status:{" "}
        {renderStatus(record.status, record)}
      </span>
      <span style={{ marginTop: "10px" }}>
        Informação:{" "}
        <Link to={`/deposito/${record.id}`}>
          <InfoCircleOutlined style={{ cursor: "pointer", height: "100%" }} />
        </Link>
      </span>
    </div>
  );
  const locale = {
    emptyText: <Empty description="Sem dados" />,
  };

  const renderError = (status) => {
    if (activeError) {
      return (
        <Result
          status={status === "500" ? "500" : "404"}
          title={status}
          subTitle={
            status === "500"
              ? "Ocorreu um erro no servidor, tente novamente mais tarde."
              : "Ocorreu um erro, tente novamente mais tarde."
          }
          extra={
            <Button onClick={() => navigate("/")} type="primary">
              Dashboard
            </Button>
          }
        />
      );
    }
  };

  return (
    <div className="DepositListing">
      <Layout
        style={{
          padding: "1.25rem",
        }}
      >
        <Breadcrumb
          style={{
            margin: "16px 0",
          }}
        >
          <div style={{ display: "flex", width: "100%", justifyContent: "space-between", alignItems: "center" }}>
            <h2 className="title-page">Meus Depósitos</h2>
          </div>
          <Divider />
          <Breadcrumb.Item>Minhas vendas</Breadcrumb.Item>
          <Breadcrumb.Item>Meus Depósitos</Breadcrumb.Item>
        </Breadcrumb>
        <Content
          style={{
            padding: 24,
            margin: 0,
            minHeight: 280,
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
          }}
        >
          <h3 className="subtitle-page">Meus Depósitos</h3>
          <Divider />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <AccountsSearchSelect
                style={{ width: '200px'}}
                placeholder="Filtrar por conta"
                value={sellerFilter}
                onChange={(value) => setSellerFilter(value)}
                allowClear={true}
              />
            </div>            <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
              <Form.Item label="Período dos depósitos" style={{ margin: 0 }}>
                <RangePicker
                  onChange={(dates) => setDateRange(dates)}
                  value={dateRange}
                  style={{ width: 300 }}
                  placeholder={['Data inicial', 'Data final']}
                  format={{ format: 'DD/MM/YYYY', type: 'mask' }}
                />
              </Form.Item>
            </div>
          </div>
          {isFetching ? (
            <Skeleton active />
          ) : activeError ? (
            renderError(errorStatus)
          ) : (
            <Table
              columns={columns}
              locale={locale}
              expandable={{
                expandedRowRender: isMobile ? expandedRowRender : undefined,
                expandedRowKeys: expandedRowKeys,
                onExpand: (expanded, record) => handleRowClick(record),
                responsive: ["xl"],
              }}
              dataSource={
                filteredData &&
                filteredData.map((item) => ({ ...item, key: item.id }))
              }
              pagination={{
                current: pagination.current,
                total: pagination.total,
                showSizeChanger: false,
              }}
              bordered
              onChange={onChangePage}
            />
          )}
          <div className="download-section">
            <Dropdown menu={reportMenuProps} disabled={downloadLoading}>
              <Button loading={downloadLoading} disabled={downloadLoading}>
                <Space>
                  Baixar Relatório
                  <DownOutlined />
                </Space>
              </Button>
            </Dropdown>
          </div>
        </Content>
      </Layout>
    </div >
  );
};

export default DepositListing;